/**
 * Planungsgeschaeft entity model class
 *
 * This file is part of eBau
 * (c) Kaden und Partner AG
 */
import {
    PlanningPermissionApplication,
    PlanningPermissionApplicationCollection
} from './PlanningPermissionApplication';
import { ECH_STATUS } from '@/models/Status.js';
import type { User } from './User';
import { createModel } from '@kp/js-model';
import { useAuthUser } from '@/helpers/globals';

export class Planungsgeschaeft extends PlanningPermissionApplication {
    type: string | null = 'pg';

    getClassName(): string {
        return 'Planungsgeschaeft';
    }

    canBeHandedOver() {
        return (
            this.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS ||
            this.aktueller_status_ebau_value === ECH_STATUS.SUSPENDED ||
            this.aktueller_status_ebau_value === ECH_STATUS.DEMAND_DOCUMENTS ||
            this.aktueller_status_ebau_value === ECH_STATUS.DEMAND_DOCUMENTS_GEMEINDE
        );
    }

    isSwitchToStateAllowed(user: User | null, state: string | null) {
        if (!user || !state) {
            return false;
        }

        if (this.aktueller_status_ebau_value === ECH_STATUS.DOSSIER_ARCHIVED) {
            return false;
        }

        switch (state) {
            case ECH_STATUS.SUBMITTED:
                return this.aktueller_status_ebau_value === ECH_STATUS.STATUS_NEW;
            case ECH_STATUS.IN_PROGRESS:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return (
                    this.aktueller_status_ebau_value === ECH_STATUS.SUBMITTED ||
                    this.aktueller_status_ebau_value === ECH_STATUS.SUSPENDED ||
                    this.aktueller_status_ebau_value === ECH_STATUS.OBJECTION_PENDING
                );
            case ECH_STATUS.WITHDRAWN:
                if (user.isRoleAdmin) {
                    return true;
                }
                if (user.isRoleFachstelle || user.isRoleGemeinde) {
                    return false;
                }
                return (
                    this.aktueller_status_ebau_value === ECH_STATUS.SUBMITTED ||
                    this.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS ||
                    this.aktueller_status_ebau_value === ECH_STATUS.SUSPENDED
                );
            case ECH_STATUS.DEMAND_DOCUMENTS:
            case ECH_STATUS.SUSPENDED:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS;
            case ECH_STATUS.APPELLANT_PROCESS:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.DECISION_ISSUED;
            case ECH_STATUS.OBJECTION_PENDING:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS;
            case ECH_STATUS.DECISION_ISSUED:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS;
            case ECH_STATUS.DECISION_LEGALLY_BINDING:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return (
                    this.aktueller_status_ebau_value === ECH_STATUS.DECISION_ISSUED ||
                    this.aktueller_status_ebau_value === ECH_STATUS.APPELLANT_PROCESS
                );
            case ECH_STATUS.BUILDING_FREEZE:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return this.aktueller_status_ebau_value === ECH_STATUS.UNDER_CONSTRUCTION;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.UNDER_CONSTRUCTION;
            case ECH_STATUS.UNDER_CONSTRUCTION:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return (
                    this.aktueller_status_ebau_value === ECH_STATUS.BUILDING_FREEZE ||
                    this.aktueller_status_ebau_value === ECH_STATUS.DECISION_LEGALLY_BINDING
                );
            case ECH_STATUS.DOSSIER_CLOSED:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return (
                    this.aktueller_status_ebau_value === ECH_STATUS.UNDER_CONSTRUCTION ||
                    this.aktueller_status_ebau_value === ECH_STATUS.WITHDRAWN ||
                    this.aktueller_status_ebau_value === ECH_STATUS.APPELLANT_PROCESS ||
                    this.aktueller_status_ebau_value === ECH_STATUS.DECISION_LEGALLY_BINDING
                );
            case ECH_STATUS.DOSSIER_ARCHIVED:
                if ((!user.isRoleGemeinde || !user.isInGemeinde(this.stelle_id)) && !user.isRoleAdmin) {
                    return false;
                }
                return this.aktueller_status_ebau_value === ECH_STATUS.DOSSIER_CLOSED;
            default:
                return false;
        }
    }

    isDeleteAllowed(user: User) {
        if (this.aktueller_status_ebau_value !== ECH_STATUS.STATUS_NEW) {
            return false;
        }

        if (user.isRoleAdmin) {
            return true;
        }

        if (this.deleted) {
            return false;
        }

        return true;
    }

    isArchiveAllowed(user: User) {
        if (this.aktueller_status_ebau_value !== ECH_STATUS.DOSSIER_CLOSED) {
            return false;
        }

        if (user.isRoleAdmin) {
            return true;
        }

        if (user.isRoleGemeinde && user.isInGemeinde(this.stelle_id)) {
            return true;
        }

        return true;
    }

    isCloseDossierAllowed(user: User) {
        if (user.isRoleFachstelle || user.isRoleGemeinde || user.isRoleAdmin) {
            return this.aktueller_status_ebau_value === ECH_STATUS.UNDER_CONSTRUCTION;
        }
    }

    isWriter(user: User) {
        if (!user) {
            return false;
        }

        return this.writers && this.writers.indexOf(user.id!) >= 0;
    }
}

export async function createNewGeschaeft(): Promise<Planungsgeschaeft> {
    const user = useAuthUser();
    let stelleId = user?.active_role_stelle_id ?? (user?.gemeinden_ids && user?.gemeinden_ids.length > 0 ? user.gemeinden_ids[0] : null);
    const planungsgeschaeft = createModel(Planungsgeschaeft, {
        type: 'pg',
        stelle_id: stelleId
    });


    await planungsgeschaeft.save();
    return planungsgeschaeft;
}

export class PlanungsgeschaeftCollection extends PlanningPermissionApplicationCollection {
    protected modelCls = Planungsgeschaeft;

    public getModelClassName(): string {
        return 'Planungsgeschaeft';
    }
}
